import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import type {
  AppController,
  ExecuteAction,
  FoodSearchResult,
  RecipeDraft,
  RecipeDetails,
} from "../types";

interface RecipeEditorProps {
  controller: AppController;
  execute: ExecuteAction;
  onCancel: () => void;
  onSaved: (recipe: FoodSearchResult) => void;
  initialRecipe?: RecipeDetails;
}

interface SelectedIngredient {
  readonly food: FoodSearchResult;
  readonly amount: string;
}

interface RecipeNutrition {
  readonly calories: number;
  readonly proteinGrams: number;
  readonly carbohydrateGrams: number;
  readonly fatGrams: number;
}

const EMPTY_NUTRITION: RecipeNutrition = Object.freeze({
  calories: 0,
  proteinGrams: 0,
  carbohydrateGrams: 0,
  fatGrams: 0,
});

export function RecipeEditor({ controller, execute, onCancel, onSaved, initialRecipe }: RecipeEditorProps) {
  const [name, setName] = useState(initialRecipe?.name ?? "");
  const [servingCount, setServingCount] = useState(() => formatInputNumber(initialRecipe?.servingCount ?? 4));
  const [servingName, setServingName] = useState(initialRecipe?.servingName ?? "");
  const [ingredients, setIngredients] = useState<SelectedIngredient[]>(() =>
    initialRecipe?.ingredients.map(({ amount, ...food }) => ({ food, amount: formatInputNumber(amount) })) ?? []
  );
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FoodSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const parsedServingCount = positiveNumber(servingCount);
  const ingredientsAreValid = ingredients.length > 0
    && ingredients.every((ingredient) => positiveNumber(ingredient.amount) !== undefined);
  const canSave = name.trim().length > 0 && parsedServingCount !== undefined && ingredientsAreValid;
  const wholeRecipeNutrition = useMemo(() => totalNutrition(ingredients), [ingredients]);
  const perServingNutrition = parsedServingCount === undefined
    ? undefined
    : scaleNutrition(wholeRecipeNutrition, 1 / parsedServingCount);
  const addedFoodIds = new Set(ingredients.map((ingredient) => ingredient.food.id));
  const portionLabel = servingName.trim() || "serving";
  const hasOpenFoodFactsResults = results.some((result) =>
    result.sourceLabel.toLocaleLowerCase().includes("open food facts")
  );

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nameInputRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  async function searchIngredients() {
    const trimmed = query.trim();
    if (trimmed.length < 2 || isSearching) return;
    setIsSearching(true);
    setHasSearched(false);
    const found = await execute(
      () => controller.searchRecipeIngredients(trimmed),
      { errorMessage: "Foods could not be searched. Please try again." },
    );
    if (found.ok) {
      setResults(found.value);
      setHasSearched(true);
    }
    setIsSearching(false);
  }

  function addIngredient(food: FoodSearchResult) {
    if (food.requiresMacroReview) return;
    setIngredients((current) => {
      if (current.some((ingredient) => ingredient.food.id === food.id)) return current;
      const defaultAmount = Number.isFinite(food.basisAmount) && food.basisAmount > 0
        ? food.basisAmount
        : 1;
      return [...current, { food, amount: formatInputNumber(defaultAmount) }];
    });
  }

  function updateIngredientAmount(foodId: string, amount: string) {
    setIngredients((current) => current.map((ingredient) =>
      ingredient.food.id === foodId ? { ...ingredient, amount } : ingredient,
    ));
  }

  function removeIngredient(foodId: string) {
    setIngredients((current) => current.filter((ingredient) => ingredient.food.id !== foodId));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const count = positiveNumber(servingCount);
    if (!canSave || count === undefined || saving) return;

    const draft: RecipeDraft = {
      id: initialRecipe?.id,
      name: name.trim(),
      servingCount: count,
      servingName: servingName.trim() || undefined,
      ingredients: ingredients.map((ingredient) => ({
        foodId: ingredient.food.id,
        amount: positiveNumber(ingredient.amount)!,
      })),
    };

    setSaving(true);
    const saved = await execute(
      () => controller.saveRecipe(draft),
      { successMessage: initialRecipe ? `${draft.name} updated.` : `${draft.name} saved to your recipes.` },
    );
    setSaving(false);
    if (saved.ok) onSaved(saved.value);
  }

  function handleDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!saving) onCancel();
      return;
    }
    if (event.key !== "Tab") return;

    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("*"))
      .filter((control) => control.matches("input:not(:disabled), button:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary"))
      .filter((control) => control.tabIndex >= 0 && !control.hidden)
      .filter((control) => control.tagName === "SUMMARY" || !control.closest("details:not([open])"));
    if (controls.length === 0) return;
    const first = controls[0];
    const last = controls.at(-1)!;
    if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-sheet recipe-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recipe-editor-heading"
        aria-describedby="recipe-editor-description"
        aria-busy={saving}
        onKeyDown={handleDialogKeyDown}
      >
        <div className="modal-sheet__handle" aria-hidden="true" />
        <div className="modal-sheet__heading">
          <div>
            <p className="eyebrow">Your recipes</p>
            <h2 id="recipe-editor-heading">{initialRecipe ? "Edit recipe" : "Create a recipe"}</h2>
            <p className="recipe-editor__description" id="recipe-editor-description">
              Combine saved or public foods, then divide the total nutrition into servings.
            </p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onCancel}
            aria-label="Close recipe editor"
            disabled={saving}
          >x</button>
        </div>

        <form className="recipe-editor__form" onSubmit={save}>
          <fieldset className="recipe-editor__form-body" disabled={saving}>
            <section className="recipe-editor__section" aria-labelledby="recipe-details-heading">
              <div className="recipe-editor__section-heading">
                <div>
                  <h3 id="recipe-details-heading">Recipe details</h3>
                  <p>Name the recipe and choose how many portions it makes.</p>
                </div>
              </div>
              <div className="recipe-editor__details-grid">
                <label className="field recipe-editor__name-field">
                  <span>Recipe name</span>
                  <input
                    ref={nameInputRef}
                    required
                    maxLength={500}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="e.g. Chicken burrito bowls"
                    autoComplete="off"
                  />
                </label>
                <label className="field">
                  <span>Makes</span>
                  <span className="input-with-suffix">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0.01"
                      step="0.01"
                      required
                      value={servingCount}
                      aria-label="Number of servings"
                      aria-invalid={parsedServingCount === undefined}
                      aria-describedby={parsedServingCount === undefined ? "recipe-serving-count-error" : undefined}
                      onChange={(event) => setServingCount(event.target.value)}
                    />
                    <i>servings</i>
                  </span>
                  {parsedServingCount === undefined && (
                    <small className="field-error" id="recipe-serving-count-error" role="alert">
                      Enter a number of servings greater than zero.
                    </small>
                  )}
                </label>
                <label className="field">
                  <span>Serving name <em>optional</em></span>
                  <input
                    maxLength={200}
                    value={servingName}
                    onChange={(event) => setServingName(event.target.value)}
                    placeholder="e.g. bowl or slice"
                    autoComplete="off"
                  />
                </label>
              </div>
            </section>

            <section className="recipe-editor__section" aria-labelledby="recipe-ingredients-heading">
              <div className="recipe-editor__section-heading">
                <div>
                  <h3 id="recipe-ingredients-heading">Ingredients</h3>
                  <p>Search your saved foods and the public food database.</p>
                </div>
                <span>{ingredients.length} {ingredients.length === 1 ? "ingredient" : "ingredients"}</span>
              </div>

              <div className="recipe-editor__search-row" role="search">
                <label className="field recipe-editor__search-field">
                  <span className="sr-only">Search foods</span>
                  <input
                    type="search"
                    value={query}
                    placeholder="Search foods"
                    autoComplete="off"
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setResults([]);
                      setHasSearched(false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      void searchIngredients();
                    }}
                  />
                </label>
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => void searchIngredients()}
                  disabled={isSearching || query.trim().length < 2}
                >
                  {isSearching ? "Searching…" : "Search"}
                </button>
              </div>
              {query.trim().length === 1 && <p className="field-hint">Enter at least two characters.</p>}

              {results.length > 0 && (
                <ul className="recipe-editor__search-results" aria-label="Ingredient search results">
                  {results.map((result) => {
                    const alreadyAdded = addedFoodIds.has(result.id);
                    return (
                      <li className="recipe-editor__search-result" key={result.id}>
                        <div className="recipe-editor__food-details">
                          <strong>{result.name}</strong>
                          <span>{[result.brand, result.basisLabel, result.sourceLabel].filter(Boolean).join(" · ")}</span>
                          <div className="macro-summary" aria-label={`${result.name} macronutrients`}>
                            <span>{formatCalories(result.nutrition.calories)} kcal</span>
                            <span>P {formatGrams(result.nutrition.proteinGrams)}</span>
                            <span>C {formatGrams(result.nutrition.carbohydrateGrams)}</span>
                            <span>F {formatGrams(result.nutrition.fatGrams)}</span>
                          </div>
                          {result.requiresMacroReview && (
                            <p className="inline-warning">Review and save this food before using it in a recipe.</p>
                          )}
                        </div>
                        <button
                          className="button button--secondary button--small"
                          type="button"
                          onClick={() => addIngredient(result)}
                          disabled={alreadyAdded || result.requiresMacroReview}
                        >
                          {alreadyAdded ? "Added" : result.requiresMacroReview ? "Review first" : "Add ingredient"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasOpenFoodFactsResults && (
                <p className="food-attribution">
                  Food data provided by <a href="https://world.openfoodfacts.org/">Open Food Facts</a>, a collaborative public food database.
                </p>
              )}

              {hasSearched && !isSearching && results.length === 0 && (
                <div className="recipe-editor__empty-search" role="status">
                  <strong>No foods found</strong>
                  <p>Try another description or create the food first.</p>
                </div>
              )}

              {ingredients.length === 0 ? (
                <div className="recipe-editor__empty-ingredients">
                  <strong>No ingredients yet</strong>
                  <p>Search above and add at least one saved food.</p>
                </div>
              ) : (
                <ul className="recipe-editor__ingredients">
                  {ingredients.map((ingredient) => {
                    const amountIsValid = positiveNumber(ingredient.amount) !== undefined;
                    const errorId = `recipe-ingredient-${ingredient.food.id}-error`;
                    return (
                      <li className="recipe-editor__ingredient" key={ingredient.food.id}>
                        <div className="recipe-editor__food-details">
                          <strong>{ingredient.food.name}</strong>
                          <span>{[ingredient.food.brand, ingredient.food.basisLabel].filter(Boolean).join(" · ")}</span>
                        </div>
                        <label className="field recipe-editor__quantity-field">
                          <span>Quantity</span>
                          <span className="input-with-suffix">
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0.01"
                              step="any"
                              required
                              value={ingredient.amount}
                              aria-label={`Quantity of ${ingredient.food.name}`}
                              aria-invalid={!amountIsValid}
                              aria-describedby={!amountIsValid ? errorId : undefined}
                              onChange={(event) => updateIngredientAmount(ingredient.food.id, event.target.value)}
                            />
                            <i>{nativeUnitLabel(ingredient.food)}</i>
                          </span>
                          {!amountIsValid && (
                            <small className="field-error" id={errorId} role="alert">
                              Enter a quantity greater than zero.
                            </small>
                          )}
                        </label>
                        <button
                          className="icon-button icon-button--danger recipe-editor__remove"
                          type="button"
                          aria-label={`Remove ${ingredient.food.name}`}
                          onClick={() => removeIngredient(ingredient.food.id)}
                        >x</button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="recipe-editor__summary" aria-labelledby="recipe-summary-heading" aria-live="polite">
              <div className="recipe-editor__section-heading">
                <div>
                  <p className="eyebrow">Live calculation</p>
                  <h3 id="recipe-summary-heading">Recipe nutrition</h3>
                </div>
              </div>
              <div className="recipe-editor__summary-grid">
                <NutritionBreakdown label="Whole recipe" nutrition={wholeRecipeNutrition} />
                <NutritionBreakdown label={`Per ${portionLabel}`} nutrition={perServingNutrition} />
              </div>
            </section>
          </fieldset>

          <div className="modal-actions recipe-editor__actions">
            <button className="button button--ghost" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={saving || !canSave}>
              {saving ? "Saving…" : initialRecipe ? "Save changes" : "Save recipe"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function NutritionBreakdown({ label, nutrition }: { label: string; nutrition?: RecipeNutrition }) {
  return (
    <article className="recipe-editor__nutrition-breakdown" aria-label={label}>
      <h4>{label}</h4>
      <dl>
        <div><dt>Calories</dt><dd>{nutrition ? `${formatCalories(nutrition.calories)} kcal` : "—"}</dd></div>
        <div><dt>Protein</dt><dd>{nutrition ? formatGrams(nutrition.proteinGrams) : "—"}</dd></div>
        <div><dt>Carbs</dt><dd>{nutrition ? formatGrams(nutrition.carbohydrateGrams) : "—"}</dd></div>
        <div><dt>Fat</dt><dd>{nutrition ? formatGrams(nutrition.fatGrams) : "—"}</dd></div>
      </dl>
    </article>
  );
}

function totalNutrition(ingredients: readonly SelectedIngredient[]): RecipeNutrition {
  return ingredients.reduce<RecipeNutrition>((total, ingredient) => {
    const amount = positiveNumber(ingredient.amount);
    const basisAmount = ingredient.food.basisAmount;
    if (amount === undefined || !Number.isFinite(basisAmount) || basisAmount <= 0) return total;
    const scale = amount / basisAmount;
    return {
      calories: total.calories + ingredient.food.nutrition.calories * scale,
      proteinGrams: total.proteinGrams + ingredient.food.nutrition.proteinGrams * scale,
      carbohydrateGrams: total.carbohydrateGrams + ingredient.food.nutrition.carbohydrateGrams * scale,
      fatGrams: total.fatGrams + ingredient.food.nutrition.fatGrams * scale,
    };
  }, EMPTY_NUTRITION);
}

function scaleNutrition(nutrition: RecipeNutrition, scale: number): RecipeNutrition {
  return {
    calories: nutrition.calories * scale,
    proteinGrams: nutrition.proteinGrams * scale,
    carbohydrateGrams: nutrition.carbohydrateGrams * scale,
    fatGrams: nutrition.fatGrams * scale,
  };
}

function positiveNumber(value: string): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function nativeUnitLabel(food: FoodSearchResult): string {
  if (food.basisUnit === "g") return "g";
  if (food.basisUnit === "ml") return "ml";
  return food.servingName?.trim() || "servings";
}

function formatInputNumber(value: number): string {
  return String(Math.round(value * 100) / 100);
}

function formatCalories(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatGrams(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} g`;
}
